import React from 'react';
import debounce from 'lodash/debounce';
import { useLocalStorageValueChangeEffect, useStore, useTabsSyncedCallback, useTabsSyncedState } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';

import { useUserActivityMonitor } from './userActivityMonitoring';
import { AutoLogoutContext } from './auto-logout-notification.context';


const FASTER_AUTO_LOGOUT = window.location.search.includes('fasterAutoLogout');
const autoLogoutDuration = FASTER_AUTO_LOGOUT ? { seconds: 10 } : { minutes: 5 };
const autoLogoutDurationMS = CalendarDateService.date.duration(autoLogoutDuration).asMilliseconds();


export enum AutoLogoutTrigger {
  logout = 'logout',
  logoutManual = 'logoutManual',
}


const useInactiveNotificationModal = (enabled: boolean, onLogout: (initiatedByUser: boolean) => void) => {
  const { timeout } = React.useContext(AutoLogoutContext);
  const onLogoutRef = React.useRef(onLogout);
  const [ isOpened, setIsOpened ] = useTabsSyncedState('isAutoLogoutNotificationOpened', false, {
    overrideWithDefaultOnMount: true,
  });
  const openModal = () => setIsOpened(true);
  const closeModal = () => setIsOpened(false);

  useUserActivityMonitor(enabled);

  const notificationModalDurationMS = React.useMemo(() => {
    const notificationModalDuration = FASTER_AUTO_LOGOUT ? { seconds: 10 } : { minutes: timeout };
    const notificationModalDurationMS = CalendarDateService.date.duration(notificationModalDuration).asMilliseconds();

    return notificationModalDurationMS;
  }, [ timeout ]);

  useLocalStorageValueChangeEffect<number>('lastUserActivity', (value, isInitial) => {
    if (!enabled) return;
    if (isOpened) return;

    const userWasInactiveFor = Date.now() - (value ?? Date.now());
    let openAfter = notificationModalDurationMS - userWasInactiveFor;

    if (userWasInactiveFor > notificationModalDurationMS + autoLogoutDurationMS && isInitial) {
      onLogoutRef.current?.(true);
      return;
    } else if (userWasInactiveFor > notificationModalDurationMS || userWasInactiveFor <= notificationModalDurationMS) {
      openAfter = notificationModalDurationMS;
    }

    const showInactiveModal = debounce(() => {
      openModal();
    }, openAfter);

    showInactiveModal();

    return () => {
      showInactiveModal.cancel();
    };
  }, [ enabled, isOpened, notificationModalDurationMS ]);

  React.useEffect(() => {
    const started = (enabled && isOpened);

    if (!started) return;

    // This will be executed when LogOut notification modal will be shown
    // so user will be automatically logged out 5 minutes after modal shown
    const autoLogout = debounce(() => {
      onLogoutRef.current?.(false);
    }, autoLogoutDurationMS);

    autoLogout();

    return () => autoLogout.cancel();
  }, [ enabled, isOpened ]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isOpened, openModal, closeModal };
};


export const useAutoLogoutNotification = (enabled: boolean) => {
  const { isOpened, closeModal } = useInactiveNotificationModal(enabled, (initiatedByUser) => {
    if (initiatedByUser) {
      triggerLogout();
      authStore.logout(environment.authUrl);
    } else {
      authStore.logout(environment.authUrl, {
        redirectAfterLogin: true,
        showAuthPrivacyError: true,
        onBeforeRedirect: () => {
          triggerLogout();
        }
      });
    }
  });
  const { authStore, environment } = useStore();

  useTabsSyncedCallback(AutoLogoutTrigger.logoutManual, () => {
    authStore.logout(environment.authUrl, {
      redirectAfterLogin: false,
      showAuthPrivacyError: false,
    });
  });

  const triggerLogout = useTabsSyncedCallback(AutoLogoutTrigger.logout, () => {
    // do not logout with redirect if page is focused
    const shouldLogoutWithRedirect = !document.hasFocus();

    closeModal();
    authStore.logout(environment.authUrl, {
      redirectAfterLogin: shouldLogoutWithRedirect,
      showAuthPrivacyError: shouldLogoutWithRedirect,
    });
  });

  const triggerStayLoggedIn = useTabsSyncedCallback('refresh-token', () => {
    closeModal();
    authStore.fetchAccessTokenByRefreshToken();
  });

  React.useEffect(() => {
    if (authStore.tokens?.refreshToken) {
      triggerStayLoggedIn();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isOpened,
    handleLogout: triggerLogout,
    handleStayLoggedIn: triggerStayLoggedIn,
  };
};
